import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Users,
  Wallet,
  BarChart3,
  Clock,
  Globe2,
  Sparkles,
  ShieldCheck,
  PieChart,
  Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from "../../services/apiClient.ts";

const Divider = () => <div className="border-t border-gray-200 dark:border-gray-800" />;

const StatCard = ({ title, value, delta, icon: Icon, accent }) => (
  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 border border-gray-200/70 dark:border-gray-700 shadow-soft p-5">
    <div className="absolute inset-0 pointer-events-none opacity-70">
      <div className={`absolute -right-10 -top-10 w-32 h-32 rounded-full blur-3xl ${accent.bg}`} />
      <div className={`absolute -left-12 bottom-0 w-28 h-28 rounded-full blur-3xl ${accent.bgAlt}`} />
    </div>
    <div className="relative flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{title}</p>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-50 mt-2">{value}</p>
        {delta && (
          <div className="inline-flex items-center gap-1 mt-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-white/70 dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-700">
            {delta.startsWith('-') ? (
              <ArrowDownRight className="w-4 h-4 text-red-500" />
            ) : (
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
            )}
            <span className={delta.startsWith('-') ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}>
              {delta}
            </span>
            <span className="text-gray-500 dark:text-gray-400">مقارنة بالشهر الماضي</span>
          </div>
        )}
      </div>
      <div className={`p-3 rounded-xl ${accent.iconBg} text-white shadow-lg`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const SparkBars = ({ data, color }) => {
  const max = useMemo(() => Math.max(...data, 1), [data]);
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((value, idx) => {
        const height = Math.max((value / max) * 100, 12);
        return (
          <div
            key={idx}
            className={`flex-1 rounded-lg ${color}`}
            style={{ height: `${height}%` }}
          />
        );
      })}
    </div>
  );
};

const Donut = ({ value, label, color }) => {
  const angle = Math.min(Math.max(value, 0), 100) * 3.6;
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative w-28 h-28 rounded-full flex items-center justify-center"
        style={{
          background: `conic-gradient(${color} ${angle}deg, #e5e7eb ${angle}deg)`,
        }}
      >
        <div className="absolute w-16 h-16 rounded-full bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700" />
        <span className="relative text-lg font-semibold text-gray-900 dark:text-gray-100">
          {value}%
        </span>
      </div>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{label}</p>
    </div>
  );
};

const MiniLegend = ({ items }) => (
  <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-300">
    {items.map((item) => (
      <div key={item.label} className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
        <span>{item.label}</span>
      </div>
    ))}
  </div>
);

const HomeDashboard = () => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fallbackData = useMemo(() => ({
    cards: {
      total_revenue: 842400,
      collected: 212300,
      remaining: 18900,
      active_travelers: 1248,
      deltas: {
        total_revenue: '+14.2%',
        collected: '+6.4%',
        active_travelers: '+3.1%',
        remaining: '-2.4%',
      }
    },
    trend: {
      months: ['2025-01','2025-02','2025-03','2025-04','2025-05','2025-06','2025-07','2025-08','2025-09','2025-10','2025-11','2025-12'],
      revenue: [62, 70, 78, 74, 80, 88, 95, 92, 104, 110, 120, 132],
      collected: [48, 55, 60, 58, 62, 70, 74, 76, 84, 90, 94, 101],
    },
    status_mix: {
      collected_pct: 64,
      pending_pct: 22,
      overdue_pct: 14,
    },
    top_packages: [
      { name: 'عمرة بريميوم', value: 68 },
      { name: 'حج VIP', value: 52 },
      { name: 'عمرة قياسية', value: 44 },
      { name: 'حج اقتصادي', value: 31 },
    ],
    upcoming: [
      { name: 'علي حسن', date: '2025-01-15', amount: 1840, status: 'due_soon' },
      { name: 'مريم النجار', date: '2025-01-17', amount: 2120, status: 'due_soon' },
      { name: 'عمر يوسف', date: '2025-01-21', amount: 950, status: 'scheduled' },
      { name: 'سارة إبراهيم', date: '2025-01-23', amount: 1420, status: 'scheduled' },
    ],
    activity: [
      { title: 'تأكيد مجموعة جديدة', meta: 'حج VIP · 24 مسافر', time: 'قبل ساعتين', amount: 0, icon: ShieldCheck },
      { title: 'استلام دفعة', meta: '12,300 SAR · بطاقة', time: 'قبل 5 ساعات', amount: 12300, icon: CreditCard },
      { title: 'انضمام مسافر', meta: 'عائشة صالح · عمرة بريميوم', time: 'قبل 7 ساعات', amount: 0, icon: Sparkles },
      { title: 'تنفيذ استرجاع', meta: 'حجز #8841', time: 'قبل يوم', amount: 0, icon: Wallet },
    ],
  }), []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/v1/dashboard/overview');
        setOverview(res.data);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'تعذر تحميل البيانات';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع`;
  };

  const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

  const payload = overview || fallbackData;

  const stats = useMemo(() => {
    const cards = payload.cards || {};
    const deltas = overview ? {} : (cards.deltas || {});
    return [
      {
        title: t('dashboard.totalRevenue', 'إجمالي الإيرادات'),
        value: formatCurrency(cards.total_revenue ?? 0),
        delta: deltas.total_revenue,
        icon: Wallet,
        accent: { bg: 'bg-primary-200', bgAlt: 'bg-secondary-200', iconBg: 'bg-primary-600' },
      },
      {
        title: t('dashboard.collected', 'المحصّل'),
        value: formatCurrency(cards.collected ?? 0),
        delta: deltas.collected,
        icon: CreditCard,
        accent: { bg: 'bg-emerald-200', bgAlt: 'bg-primary-100', iconBg: 'bg-emerald-500' },
      },
      {
        title: t('dashboard.activeTravelers', 'المسافرون النشطون'),
        value: formatNumber(cards.active_travelers ?? 0),
        delta: deltas.active_travelers,
        icon: Users,
        accent: { bg: 'bg-cyan-200', bgAlt: 'bg-indigo-200', iconBg: 'bg-cyan-500' },
      },
      {
        title: t('dashboard.overdue', 'المتبقي/المتأخر'),
        value: formatCurrency(cards.remaining ?? 0),
        delta: deltas.remaining,
        icon: Clock,
        accent: { bg: 'bg-orange-200', bgAlt: 'bg-rose-100', iconBg: 'bg-orange-500' },
      },
    ];
  }, [payload, overview, t]);

  const revenueTrend = payload.trend?.revenue ?? fallbackData.trend.revenue;
  const collectionTrend = payload.trend?.collected ?? fallbackData.trend.collected;
  const rawTopPackages = payload.top_packages || fallbackData.top_packages || [];
  const topTotal = rawTopPackages.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
  const topPackages = rawTopPackages.map((item, idx) => {
    const percent = topTotal > 0 ? Math.round(((Number(item.value) || 0) / topTotal) * 100) : 0;
    return {
      ...item,
      percent,
      color: ['#2563eb', '#ec4899', '#10b981', '#f59e0b', '#22c55e', '#a855f7'][idx % 6],
    };
  });
  const statusMixData = payload.status_mix || fallbackData.status_mix;
  const statusMix = [
    { label: t('dashboard.collected', 'تم التحصيل'), value: statusMixData.collected_pct ?? 0, color: '#10b981' },
    { label: t('dashboard.pending', 'قيد الانتظار'), value: statusMixData.pending_pct ?? 0, color: '#2563eb' },
    { label: t('dashboard.overdue', 'متأخر'), value: statusMixData.overdue_pct ?? 0, color: '#ef4444' },
  ];
  const isLiveUpcoming = Boolean(overview && overview.upcoming && overview.upcoming.length);
  const upcoming = isLiveUpcoming ? overview.upcoming : fallbackData.upcoming;
  const activity = payload.activity && payload.activity.length ? payload.activity : fallbackData.activity;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 mx-auto mb-4 border-4 border-primary-500 border-t-transparent rounded-full" />
          <p className="text-gray-600 dark:text-gray-300">جاري تحميل لوحة القيادة...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center text-red-600 dark:text-red-300 font-bold">
            !
          </div>
          <div>
            <h3 className="text-red-800 dark:text-red-300 font-semibold mb-1">تعذر تحميل البيانات</h3>
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="max-w-7xl mx-auto p-6 lg:p-8 space-y-8 rtl text-right">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-primary-600 dark:text-primary-400 font-semibold flex items-center gap-2 rtl:flex-row-reverse">
            <Globe2 className="w-4 h-4" />
            {t('dashboard.subtitle', 'نبض الأداء في مناسك')}
          </p>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mt-1">
            {t('dashboard.title', 'لوحة القيادة التنفيذية')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-2xl">
            {t('dashboard.description', 'تابع التحصيل، تفاعل المسافرين، وأقساطك القادمة في عرض واحد ملون وجاهز للإجراءات.')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Left column: collection + Iraq summary stacked */}
        <div className="xl:col-span-8 space-y-6">
          <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-medium p-6 space-y-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  {t('dashboard.collectionTitle', 'سرعة التحصيل')}
                </p>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('dashboard.collectionSubtitle', 'الإيراد مقابل التحصيل')}
                </h3>
              </div>
              <MiniLegend
                items={[
                  { label: t('dashboard.revenue', 'الإيراد'), color: '#2563eb' },
                  { label: t('dashboard.collected', 'التحصيل'), color: '#10b981' },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-3">
                <div className="rounded-xl bg-gradient-to-b from-primary-50 to-white dark:from-primary-900/20 dark:to-gray-900 border border-primary-100/70 dark:border-primary-900/40 p-4">
                  <SparkBars data={revenueTrend} color="bg-primary-500/80" />
                </div>
                <div className="rounded-xl bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-900 border border-emerald-100/70 dark:border-emerald-900/40 p-4">
                  <SparkBars data={collectionTrend} color="bg-emerald-500/80" />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{t('dashboard.revenue', 'الإيراد')}</p>
                    <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                      {formatCurrency(payload.cards?.total_revenue ?? 0)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                      <ArrowUpRight className="w-4 h-4 text-emerald-500" /> {t('dashboard.trend', 'بزيادة 9.1%')}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{t('dashboard.collected', 'التحصيل')}</p>
                    <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(payload.cards?.collected ?? 0)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                      <ArrowUpRight className="w-4 h-4 text-emerald-500" /> {t('dashboard.trend', 'بزيادة 6.3%')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-between gap-6">
                <Donut value={Math.round(statusMixData.collected_pct ?? 0)} label={t('dashboard.collected', 'تم التحصيل')} color="#10b981" />
                <Divider />
                <div className="space-y-3">
                  {statusMix.map((item) => (
                    <div key={item.label} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-600 dark:text-gray-300 font-medium">{item.label}</span>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{item.value}%</span>
                    </div>
                  ))}
                </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-primary-50 dark:bg-primary-900/15 border border-primary-100 dark:border-primary-800">
                    <ShieldCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                      {t('dashboard.tip', 'التذكيرات الآلية تحافظ على المتأخر في حدوده هذا الشهر.')}
                    </p>
                  </div>
                </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-medium p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.performance', 'أداء العراق')}</p>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.regions', 'ملخص العراق')}</h3>
              </div>
            </div>
            <div className="mt-5 space-y-4">
              {[
                {
                  label: 'نسبة التحصيل',
                  value: `${Math.round(statusMixData.collected_pct ?? 0)}%`,
                  bar: Math.min(Math.max(statusMixData.collected_pct ?? 0, 0), 100),
                  color: 'from-primary-500 to-primary-600',
                },
                {
                  label: 'إجمالي الحجوزات',
                  value: formatCurrency(payload.cards?.total_revenue ?? 0),
                  bar: 100,
                  color: 'from-secondary-500 to-secondary-600',
                },
                {
                  label: 'متوسط تذكرة المسافر',
                  value: formatCurrency((payload.cards?.total_revenue ?? 0) / Math.max(payload.cards?.active_travelers ?? 1, 1)),
                  bar: Math.min(100, Math.max(0, ((payload.cards?.total_revenue ?? 0) > 0 && (payload.cards?.active_travelers ?? 0) > 0) ? 65 : 35)),
                  color: 'from-emerald-500 to-emerald-600',
                },
                {
                  label: 'نسبة المتأخر',
                  value: `${Math.round(statusMixData.overdue_pct ?? 0)}%`,
                  bar: Math.min(Math.max(statusMixData.overdue_pct ?? 0, 0), 100),
                  color: 'from-amber-500 to-amber-600',
                },
              ].map((item) => (
                <div key={item.label} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200">
                    <span className="font-semibold">{item.label}</span>
                    <span className="font-bold">{item.value}</span>
                  </div>
                  <div className="mt-3 h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${item.color}`} style={{ width: `${item.bar}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column: stacked cards */}
        <div className="xl:col-span-4 space-y-6">
          <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-medium p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.topPackages', 'أعلى الباقات')}</p>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.byRevenue', 'حسب الإيراد')}</h3>
              </div>
              <PieChart className="w-5 h-5 text-primary-500" />
            </div>
            <div className="mt-5 space-y-3">
              {topPackages.map((item) => (
                <div key={item.name} className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200">
                    <span className="font-semibold">{item.name}</span>
                    <span className="font-bold">{item.percent}%</span>
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${item.percent}%`, backgroundColor: item.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-medium p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.upcoming', 'الأقساط القادمة')}</p>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.next14', 'خلال 14 يوماً')}</h3>
              </div>
              <Activity className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{t('dashboard.upcoming', 'الأقساط القادمة')}</span>
                <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-lg border ${
                  isLiveUpcoming
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
                    : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
                }`}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isLiveUpcoming ? '#10b981' : '#f59e0b' }} />
                  {isLiveUpcoming ? 'بيانات حية' : 'عرض تجريبي'}
                </span>
              </div>
              {upcoming.map((item) => {
                const statusLabel = item.status === 'due_soon' ? 'قريب الاستحقاق' : 'مجدول';
                const formattedDate = item.date ? new Date(item.date).toLocaleDateString('ar-IQ') : '-';
                return (
                  <div key={`${item.name}-${item.date}`} className="flex items-center justify-between bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold">
                        {(item.name || '').slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{statusLabel}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(item.amount)}</p>
                      <p className="text-xs text-primary-600 dark:text-primary-400">{formattedDate}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-medium p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.activity', 'تغذية النشاط')}</p>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.recent', 'أحدث التحديثات')}</h3>
              </div>
              <BarChart3 className="w-5 h-5 text-secondary-500" />
            </div>
            <div className="space-y-4">
              {activity.map((item) => {
                const Icon = item.icon || CreditCard;
                const timeLabel = item.time
                  ? new Date(item.time).toLocaleString('ar-IQ')
                  : item.time || '';
                return (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-secondary-50 dark:bg-secondary-900/15 text-secondary-600 dark:text-secondary-400">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.meta}</p>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{timeLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeDashboard;
